import {createReducer, on} from '@ngrx/store';
import {DevicePendingActionState} from '@birco/rainmesh-api';
import {initialState} from './pump.state';
import {PumpActions} from './pump.actions';
import {DeviceActions} from '../../devices/state/device.actions';
import {DeviceResponseDto} from '../../devices/models/device-response.interface';

const mergePendingFields = (
  currentDevice: DeviceResponseDto | undefined,
  incomingDevice: DeviceResponseDto,
): DeviceResponseDto => {
  const hasPendingValue = Object.prototype.hasOwnProperty.call(incomingDevice, 'pendingValue');
  const hasPendingState = Object.prototype.hasOwnProperty.call(incomingDevice, 'pendingState');
  const hasPendingAttemptCount = Object.prototype.hasOwnProperty.call(incomingDevice, 'pendingAttemptCount');

  return {
    ...currentDevice,
    ...incomingDevice,
    pendingValue: hasPendingValue ? (incomingDevice.pendingValue ?? undefined) : currentDevice?.pendingValue,
    pendingState: hasPendingState ? (incomingDevice.pendingState ?? undefined) : currentDevice?.pendingState,
    pendingAttemptCount: hasPendingAttemptCount ? (incomingDevice.pendingAttemptCount ?? undefined) : currentDevice?.pendingAttemptCount,
  };
};

export const pumpReducer = createReducer(
  initialState,

  on(PumpActions.public.updatePumpFilter, (state, {filter}) => ({
    ...state,
    filter: filter
  })),

  on(PumpActions.private.loadPumpsWithFilters, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(PumpActions.private.loadPumpsSuccess, (state, {devicesResponseDto}) => {
    const entities = {...state.entities};
    devicesResponseDto.devices.forEach((it) => {
      entities[it.id!!] = mergePendingFields(entities[it.id!!], it);
    });

    return {
      ...state,
      entities: entities,
      pages: {
        ...state.pages,
        [state.filter!!.page!!]: devicesResponseDto.devices.map((it) => it.id!!),
      },
      totalCount: devicesResponseDto.totalCount,
      isLoading: false,
      error: null,
    };
  }),

  on(PumpActions.private.loadPumpsFailure, (state, {error}) => ({
    ...state,
    isLoading: false,
    error: error,
  })),

  on(PumpActions.public.triggerPumpAction, (state, props) => {
    const entities = {...state.entities};
    const currentPump = entities[props.id];

    if (currentPump) {
      entities[props.id] = {
        ...currentPump,
        pendingValue: props.value,
        pendingState: DevicePendingActionState.QUEUED,
        pendingAttemptCount: currentPump.pendingAttemptCount ?? 0,
      };
    }

    return {
      ...state,
      entities,
      isLoading: true,
      error: null,
    };
  }),

  on(PumpActions.private.triggerPumpActionSuccess, (state, props) => {
    const entities = {...state.entities};
    entities[props.responseDto.id] = mergePendingFields(entities[props.responseDto.id], props.responseDto);

    return {
      ...state,
      entities,
      isLoading: false,
      error: null,
    };
  }),

  on(PumpActions.private.triggerPumpActionFailure, (state, props) => {
    const entities = {...state.entities};
    const currentPump = entities[props.id];

    if (currentPump?.pendingValue === props.attemptedValue) {
      entities[props.id] = {
        ...currentPump,
        pendingValue: undefined,
        pendingState: undefined,
        pendingAttemptCount: undefined,
      };
    }

    return {
      ...state,
      entities,
      isLoading: false,
      error: props.error,
    };
  }),

  on(PumpActions.public.updatePump, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(PumpActions.private.updatePumpSuccess, (state, {responseDto}) => {
    const entities = {...state.entities};
    entities[responseDto.id] = mergePendingFields(entities[responseDto.id], responseDto);

    return {
      ...state,
      entities,
      isLoading: false,
      error: null,
    };
  }),

  on(PumpActions.private.updatePumpFailure, (state, {error}) => ({
    ...state,
    isLoading: false,
    error: error,
  })),

  on(
    PumpActions.public.loadConnectedValves,
    PumpActions.public.replaceConnectedValves,
    (state) => ({
      ...state,
      isConnectedValvesLoading: true,
      error: null,
    })),

  on(
    PumpActions.private.loadConnectedValvesSuccess,
    PumpActions.private.replaceConnectedValvesSuccess,
    (state, {pumpId, devicesResponseDto}) => ({
      ...state,
      connectedValves: {
        ...state.connectedValves,
        [pumpId]: devicesResponseDto.devices,
      },
      isConnectedValvesLoading: false,
      error: null,
    })),

  on(
    PumpActions.private.loadConnectedValvesFailure,
    PumpActions.private.replaceConnectedValvesFailure,
    (state, {error}) => ({
      ...state,
      isConnectedValvesLoading: false,
      error: error,
    })),

  on(DeviceActions.public.refreshDeviceDataFromWebSocket, (state, props) => {
    const currentPump = state.entities[props.device.id];
    if (!currentPump) {
      return state;
    }

    const entities = {...state.entities};
    const hasPendingValue = Object.prototype.hasOwnProperty.call(props.device, 'pendingValue');
    const hasPendingState = Object.prototype.hasOwnProperty.call(props.device, 'pendingState');
    const hasPendingAttemptCount = Object.prototype.hasOwnProperty.call(props.device, 'pendingAttemptCount');

    entities[props.device.id] = {
      ...currentPump,
      id: props.device.id,
      value: props.device.value,
      pendingValue: hasPendingValue ? (props.device.pendingValue ?? undefined) : currentPump.pendingValue,
      pendingState: hasPendingState ? (props.device.pendingState ?? undefined) : currentPump.pendingState,
      pendingAttemptCount: hasPendingAttemptCount ? (props.device.pendingAttemptCount ?? undefined) : currentPump.pendingAttemptCount,
      lastSeenDate: props.device.lastSeenDate
    };

    return {
      ...state,
      entities,
    }
  }),
);
